import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/auth';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import Logo from '@/components/Logo';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { debugAuthState } from '@/utils/jwt-roles';
import { cn } from '@/lib/utils';

function decodeJwt(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch (e) {
    console.error("JWT decode error:", e);
    return null;
  }
}

const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const { isAuthenticated, loading, login, signup, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const getRedirectTarget = () => {
    const redirectParam = searchParams.get('redirect_to');
    if (redirectParam) {
      return decodeURIComponent(redirectParam);
    }
    
    const storedPath = sessionStorage.getItem('redirectPath');
    if (storedPath && storedPath !== '/' && storedPath !== '/auth') {
      sessionStorage.removeItem('redirectPath');
      return storedPath;
    }
    
    return '/dashboard';
  };

  useEffect(() => {
    if (isAuthenticated && !loading) {
      const redirectTo = getRedirectTarget();
      console.log('Auth: User is authenticated, redirecting to', redirectTo);
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, location.search]);

  useEffect(() => {
    const checkForOAuthRedirect = async () => {
      console.group("===== OAUTH DEBUGGING =====");
      
      console.log("URL parameters:", {
        hasAccessToken: !!searchParams.get('access_token'),
        hasRefreshToken: !!searchParams.get('refresh_token'),
        hasError: !!searchParams.get('error'),
        errorMessage: searchParams.get('error_description'),
        fullURL: window.location.href.split('?')[0] + '?[redacted]'
      });
      
      try {
        const accessToken = searchParams.get('access_token');
        const refreshToken = searchParams.get('refresh_token');
        
        if (accessToken || refreshToken) {
          console.log("📍 OAuth tokens detected, processing...");
          
          window.history.replaceState({}, document.title, window.location.pathname);
          
          try {
            console.log("📍 Getting user with tokens...");
            setIsLoading(true);
            const { data: userData, error: userError } = await supabase.auth.getUser();
            
            console.log("📍 Auth.getUser result:", {
              success: !userError,
              hasUser: !!userData?.user,
              userId: userData?.user?.id,
              error: userError?.message
            });
            
            if (userError) {
              console.error("Error getting user after OAuth redirect:", userError);
              toast({
                variant: "destructive",
                title: "Authentication error",
                description: "There was a problem with your authentication. Please try again.",
              });
            } else {
              console.log("Successfully authenticated with OAuth provider");
              
              if (userData.user) {
                console.log("📍 Checking for existing profile for user:", userData.user.id);
                
                try {
                  console.log("📍 Testing direct table query...");
                  const { data: testData, error: testError } = await supabase
                    .from('profiles')
                    .select('count(*)')
                    .limit(1);
                    
                  let countValue: string = 'No count data';
                  if (!testError && testData) {
                    if (Array.isArray(testData) && testData.length > 0 && 'count' in testData[0]) {
                      countValue = String(testData[0].count);
                    } else {
                      countValue = `Unexpected data format: ${JSON.stringify(testData)}`;
                    }
                  }
                  
                  console.log("📍 Direct count query result:", {
                    success: !testError,
                    count: countValue,
                    error: testError?.message,
                    code: testError?.code,
                    details: testError?.details
                  });
                } catch (testErr) {
                  console.error("📍 Test query failed:", testErr);
                }
                
                const { data: existingProfile, error: profileError } = await supabase
                  .from('profiles')
                  .select('*')
                  .eq('id', userData.user.id)
                  .single();
                  
                console.log("📍 Profile check result:", {
                  success: !profileError,
                  hasProfile: !!existingProfile,
                  error: profileError?.message,
                  errorCode: profileError?.code,
                  details: profileError?.details
                });
                
                if (profileError || !existingProfile) {
                  console.log("📍 Profile not found or error:", profileError);
                  console.log("📍 Attempting to create profile for user:", userData.user.id);
                  
                  const { error: insertError } = await supabase
                    .from('profiles')
                    .insert([{ 
                      id: userData.user.id, 
                      email: userData.user.email || '',
                      name: userData.user.user_metadata?.name || userData.user.user_metadata?.full_name || '',
                      role: 'user'
                    }]);
                    
                  console.log("📍 Profile creation result:", {
                    success: !insertError,
                    error: insertError?.message,
                    code: insertError?.code,
                    details: insertError?.details
                  });
                } else {
                  console.log("📍 Found existing profile for user:", userData.user.id);
                }
              }
              
              sessionStorage.removeItem('redirectPath');
            }
          } catch (e) {
            console.error("📍 OAuth processing error:", e);
          } finally {
            setIsLoading(false);
          }
        }
      } catch (outerError) {
        console.error("📍 OAuth callback critical error:", outerError);
      } finally {
        console.groupEnd();
      }
    };
    
    checkForOAuthRedirect();
  }, [searchParams]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.group("🔐 Login Attempt");
    console.log("Email:", email ? `${email.substring(0, 3)}...` : 'empty');
    console.log("Password:", password ? '********' : 'empty');
    
    if (!email || !password) {
      console.log("Missing email or password, showing error toast");
      console.groupEnd();
      
      toast({
        variant: "destructive", 
        title: "Error",
        description: "Email and password are required",
      });
      return;
    }
    
    try {
      console.log("Beginning login process...");
      setIsLoading(true);
      
      // Call the login function from AuthContext
      console.log("Calling login from AuthContext");
      await login(email, password);
      
      console.log("Login function completed successfully");
      
      if (sessionStorage.getItem('redirectPath')) {
        const redirectTo = getRedirectTarget();
        console.log('Post-login redirect to:', redirectTo);
        navigate(redirectTo, { replace: true });
      }
      
      console.log("Login process completed");
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
      console.groupEnd();
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password || !name) {
      toast({
        variant: "destructive", 
        title: "Error",
        description: "Name, email and password are required",
      });
      return;
    }
    
    try {
      setIsLoading(true);
      await signup(email, password, name);
    } catch (error) {
      console.error('Signup error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setIsGoogleLoading(true);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth`,
          queryParams: {
            prompt: 'select_account'
          }
        }
      });
      
      if (error) {
        throw error;
      }
      
    } catch (error) {
      console.error('Google login error:', error);
      toast({
        variant: "destructive",
        title: "Google login failed",
        description: error instanceof Error ? error.message : "An error occurred during Google login",
      });
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const debugJwtToken = async () => {
    try {
      console.group("===== JWT TOKEN DEBUG =====");
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error("Session error:", error);
        return;
      }
      
      if (!data.session) {
        console.log("No active session");
        return;
      }
      
      const token = data.session.access_token;
      if (!token) {
        console.log("No access token in session");
        return;
      }
      
      const payload = decodeJwt(token);
      if (!payload) {
        console.log("Failed to decode token");
        return;
      }
      
      console.log("JWT Payload:", payload);
      console.log("Role in token:", {
        directRole: payload.role,
        appMetadataRole: payload.app_metadata?.role,
        userMetadataRole: payload.user_metadata?.role
      });
    } catch (e) {
      console.error("Token debug error:", e);
    } finally {
      console.groupEnd();
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      debugJwtToken();
      debugAuthState();
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center">
      <div className="absolute inset-0 bg-[url('https://cdn.prod.website-files.com/662c6cfb2c22a8f121fcc48e/6781db11858c0dda7c208e42_Home%20Hero%20tiny.png')] bg-cover bg-center opacity-[0.22] image-overlay-layer light blur" />
      
      <div className="container relative flex flex-col items-center justify-center lg:px-0 animate-fade-in">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px] md:w-[450px]">
          <div className="flex flex-col items-center space-y-2 text-center mb-8">
            <Logo size="lg" className="mb-2" />
            <h1 className="text-2xl font-bold tracking-tight">
              Marketing Hub
            </h1>
            <p className="text-sm text-semibold max-w-xs">
              Manage Customer Data, configure SMS and Email campaigns for your restaurant. Access reports & insights on your online orders.
            </p>
          </div>

          {sessionStorage.getItem('redirectPath') && (
            <div className="bg-muted p-3 rounded-md text-sm mb-4">
              <p>You'll be redirected after login: <span className="font-medium">{sessionStorage.getItem('redirectPath')}</span></p>
            </div>
          )}

          <Card className="border-none shadow-soft">
            <CardContent className="pt-5">
              <Tabs defaultValue="login" value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="login">Login</TabsTrigger>
                  <TabsTrigger value="signup">Sign Up</TabsTrigger>
                </TabsList>

                <TabsContent value="login" className="mt-2">
                  {/* Add the "Forgot password?" link outside the form but positioned correctly */}
                  <div className="flex justify-end mb-0">
                    <Button 
                      type="button"
                      variant="link" 
                      size="sm" 
                      className="text-xs h-auto p-0 font-normal text-muted-foreground"
                      onClick={async () => {
                        if (!email) {
                          toast({
                            variant: "destructive",
                            title: "Email required",
                            description: "Please enter your email address first.",
                          });
                          return;
                        }
                        
                        try {
                          setIsLoading(true);
                          // Send password reset email with more explicit redirect configuration
                          const { error } = await supabase.auth.resetPasswordForEmail(
                            email, 
                            { 
                              redirectTo: `${window.location.origin}/auth/reset-password`,
                              // Make sure URL is properly registered in Supabase dashboard
                              // Authentication → URL Configuration → Redirect URLs
                            }
                          );
                          
                          if (error) throw error;
                          
                          toast({
                            title: "Password reset email sent",
                            description: "Check your email for a link to reset your password.",
                          });
                        } catch (error) {
                          console.error("Password reset error:", error);
                          toast({
                            variant: "destructive",
                            title: "Failed to send reset email",
                            description: error instanceof Error ? error.message : "An unknown error occurred",
                          });
                        } finally {
                          setIsLoading(false);
                        }
                      }}
                    >
                      Forgot password?
                    </Button>
                  </div>
                  <form 
                    onSubmit={(e) => {
                      console.log("🔔 Login form submitted");
                      handleLogin(e);
                    }} 
                    className="space-y-4"
                  >
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input 
                        id="email" 
                        type="email" 
                        placeholder="your@email.com" 
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label htmlFor="password">Password</Label>
                      </div>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pr-10"
                          required
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className="sr-only">
                            {showPassword ? 'Hide password' : 'Show password'}
                          </span>
                        </Button>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full relative bg-brand-yellow hover:bg-brand-yellow-light-1 text-brand-dark-text [&_svg]:text-brand-dark-text [&_svg]:stroke-brand-dark-text" 
                      disabled={isLoading}
                      onClick={(e) => {
                        // This ensures the login button explicitly triggers the login function
                        e.preventDefault();
                        console.log("Login button clicked");
                        handleLogin(e);
                      }}
                    >
                      <span className={isLoading ? "opacity-0" : "opacity-100"}>
                        Login
                      </span>
                      {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Loader2 className="h-5 w-5 animate-spin" />
                        </div>
                      )}
                    </Button>
                    
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <Separator className="w-full" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">
                          Or continue with
                        </span>
                      </div>
                    </div>
                    
                    <Button 
                      type="button"
                      variant="outline" 
                      className="w-full relative"
                      onClick={handleGoogleLogin}
                      disabled={isGoogleLoading}
                    >
                      <span className={cn("inline-flex items-center", isGoogleLoading ? "opacity-0" : "opacity-100")}>
                        <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                          <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
                          <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
                          <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
                          <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
                        </svg>
                        Sign in with Google
                      </span>
                      {isGoogleLoading && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Loader2 className="h-5 w-5 animate-spin" />
                        </div>
                      )}
                    </Button>
                    
                    <div className="pt-4">
                      <p className="text-center text-sm text-muted-foreground">
                        For demo, use: <span className="font-medium text-foreground">owner@restaurant.com</span> / <span className="font-medium text-foreground">password</span>
                      </p>
                    </div>
                  </form>
                </TabsContent>

                <TabsContent value="signup" className="mt-2">
                  <form onSubmit={handleSignup} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name</Label>
                      <Input 
                        id="name" 
                        type="text" 
                        placeholder="John Doe" 
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <Input 
                        id="signup-email" 
                        type="email" 
                        placeholder="your@email.com" 
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password</Label>
                      <div className="relative">
                        <Input
                          id="signup-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pr-10"
                          required
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className="sr-only">
                            {showPassword ? 'Hide password' : 'Show password'}
                          </span>
                        </Button>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full relative bg-brand-yellow hover:bg-brand-yellow-light-1 text-brand-dark-text [&_svg]:text-brand-dark-text [&_svg]:stroke-brand-dark-text" 
                      disabled={isLoading}
                    >
                      <span className={isLoading ? "opacity-0" : "opacity-100"}>
                        Create Account
                      </span>
                      {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Loader2 className="h-5 w-5 animate-spin" />
                        </div>
                      )}
                    </Button>
                    
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <Separator className="w-full" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">
                          Or continue with
                        </span>
                      </div>
                    </div>
                    
                    <Button 
                      type="button"
                      variant="outline" 
                      className="w-full relative"
                      onClick={handleGoogleLogin}
                      disabled={isGoogleLoading}
                    >
                      <span className={cn("inline-flex items-center", isGoogleLoading ? "opacity-0" : "opacity-100")}>
                        <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                          <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
                          <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
                          <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
                          <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
                        </svg>
                        Sign up with Google
                      </span>
                      {isGoogleLoading && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Loader2 className="h-5 w-5 animate-spin" />
                        </div>
                      )}
                    </Button>
                    
                    <p className="text-center text-sm text-muted-foreground mt-4">
                      By signing up, you agree to our terms of service and privacy policy.
                    </p>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>

      <div style={{ position: 'fixed', bottom: 10, right: 10, zIndex: 9999 }}>
        <Button
          variant="ghost"
          size="sm"
          onClick={async () => {
            console.group("===== MANUAL DEBUG =====");
            
            try {
              console.log("Getting current session...");
              const { data, error } = await supabase.auth.getSession();
              console.log("Session result:", { 
                success: !error, 
                hasSession: !!data.session,
                userId: data.session?.user?.id,
                error: error?.message 
              });
              
              if (data.session) {
                const token = data.session.access_token;
                if (token) {
                  try {
                    const payload = decodeJwt(token);
                    console.log("JWT role data:", {
                      appMetadataRole: payload?.app_metadata?.role,
                      directRole: payload?.role,
                      hasAppMetadata: !!payload?.app_metadata,
                      appMetadataKeys: payload?.app_metadata ? Object.keys(payload?.app_metadata) : []
                    });
                  } catch (jwtErr) {
                    console.error("JWT decode error:", jwtErr);
                  }
                }
                
                console.log("Testing profile fetch...");
                const { data: profile, error: profileError } = await supabase
                  .from('profiles')
                  .select('*')
                  .eq('id', data.session.user.id)
                  .single();
                
                console.log("Profile fetch:", { 
                  success: !profileError,
                  profile, 
                  error: profileError?.message 
                });
                
                debugAuthState();
              }
            } catch (e) {
              console.error("Debug tests error:", e);
            }
            
            console.groupEnd();
          }}
        >
          🔍
        </Button>
      </div>
    </div>
  );
};

export default Auth;
